<app-header></app-header>

<div class="report-editor" *ngIf="report">
  <div class="editor-header">
    <div class="editor-title">
      <button class="button" (click)="back()">&larr; Reports</button>
      <h1>{{isNew ? 'New Report' : report.name}}</h1>
      <span class="inactive-badge" *ngIf="report.active === false">Inactive</span>
    </div>
    <div class="editor-actions">
      <button class="button red" *ngIf="!isNew && report.active !== false" (click)="deactivate()">Deactivate</button>
      <button class="button" [disabled]="saving" (click)="save()">{{isNew ? 'Create Report' : 'Save Report'}}</button>
    </div>
  </div>

  <app-tabs>
    <app-tabpage title="General">
      <div class="form-grid">
        <label>
          Name
          <input type="text" placeholder="report name" [(ngModel)]="report.name" />
        </label>
        <label>
          Key
          <input type="text" placeholder="auto-generated when blank" [(ngModel)]="report.key" />
        </label>
        <label>
          Category
          <input type="text" placeholder="menu category" [(ngModel)]="report.category" />
        </label>
        <label class="wide">
          Description
          <input type="text" placeholder="report description" [(ngModel)]="report.description" />
        </label>
        <label>
          Type
          <select [(ngModel)]="report.type">
            <option *ngFor="let reportType of reportTypes" [value]="reportType.key">{{reportType.name}}</option>
          </select>
        </label>
        <label>
          Subtype
          <input type="text" placeholder="module name for custom/specialty" [(ngModel)]="report.subtype" />
        </label>
        <label>
          Collection
          <input type="text" placeholder="MongoDB collection" [(ngModel)]="report.collection" />
        </label>
        <label>
          Location Path
          <input type="text" placeholder="defaults to locationId" [(ngModel)]="report.locationPath" />
        </label>
        <label>
          Date Path
          <input type="text" placeholder="field used for date filtering" [(ngModel)]="report.datePath" />
        </label>
        <label>
          Default Date Range
          <input type="text" placeholder="picker range, e.g. Last 7 Days" [(ngModel)]="report.defaultDateRange" />
        </label>
        <label>
          Search Paths
          <input type="text" placeholder="always-searched paths, e.g. barcode, sku" [ngModel]="searchPathsText" (ngModelChange)="searchPathsChanged($event)" />
        </label>
        <label>
          Group Path
          <input type="text" placeholder="grouping field for total reports" [(ngModel)]="report.groupPath" />
        </label>
        <label>
          Records Path
          <input type="text" placeholder="nested records array path" [(ngModel)]="report.recordsPath" />
        </label>
        <label>
          Search Index
          <input type="text" placeholder="text search index name" [(ngModel)]="report.searchIndex" />
        </label>
        <label>
          Chunk Size
          <input type="number" [(ngModel)]="report.chunkSize" />
        </label>
      </div>

      <div class="flag-row">
        <label class="flag">
          <app-toggle [checked]="report.active !== false" (valueChanged)="report.active = $event"></app-toggle>
          Active
        </label>
        <label class="flag">
          <app-toggle [checked]="!!report.unlisted" (valueChanged)="report.unlisted = $event"></app-toggle>
          Unlisted
        </label>
        <label class="flag">
          <app-toggle [checked]="!!report.default" (valueChanged)="report.default = $event"></app-toggle>
          Default
        </label>
        <label class="flag">
          <app-toggle [checked]="!!report.secondarySort" (valueChanged)="report.secondarySort = $event"></app-toggle>
          Secondary Sort
        </label>
        <label class="flag">
          <app-toggle [checked]="!!report.supportsIncrementalLoading" (valueChanged)="report.supportsIncrementalLoading = $event"></app-toggle>
          Incremental Loading
        </label>
        <label class="flag">
          <app-toggle [checked]="!!report.secondaryReadPreference" (valueChanged)="report.secondaryReadPreference = $event"></app-toggle>
          Secondary Read Preference
        </label>
      </div>
    </app-tabpage>

    <app-tabpage title="Fields">
      <div class="section-header">
        <h3>Fields</h3>
        <button class="button" (click)="addField()">Add Field</button>
      </div>

      <app-field-editor *ngFor="let field of report.fields; let i = index"
        [field]="field" mode="report" [index]="i" [count]="report.fields.length"
        (remove)="removeField(i)" (moveUp)="moveField(i, -1)" (moveDown)="moveField(i, 1)">
      </app-field-editor>

      <div class="section-header">
        <h3>Active Fields</h3>
      </div>
      <p class="hint">Fields displayed by default when the report loads.</p>
      <div class="active-field-list">
        <label class="flag" *ngFor="let field of report.fields">
          <input type="checkbox" [checked]="isActiveField(field.path)"
            (change)="toggleActiveField(field.path, $event.target.checked)" />
          {{field.name || field.path}}
        </label>
        <label class="flag orphan" *ngFor="let path of orphanedActiveFields">
          <input type="checkbox" checked (change)="toggleActiveField(path, $event.target.checked)" />
          {{path}} (no matching field)
        </label>
      </div>

      <div class="section-header">
        <h3>Computed Fields</h3>
        <button class="button" (click)="addComputedField()">Add Computed Field</button>
      </div>
      <div class="computed-field-row" *ngFor="let computed of report.computedFields; let i = index">
        <input type="text" placeholder="output path" [(ngModel)]="computed.path" />
        <select [(ngModel)]="computed.action">
          <option value="divide">divide</option>
        </select>
        <input type="text" placeholder="dividend path" [(ngModel)]="computed.inputs[0]" />
        <span>&divide;</span>
        <input type="text" placeholder="divisor path" [(ngModel)]="computed.inputs[1]" />
        <button class="button small red" (click)="removeComputedField(i)">Remove</button>
      </div>
    </app-tabpage>

    <app-tabpage title="Filters &amp; Sorts">
      <div class="section-header">
        <h3>Default Filters</h3>
      </div>
      <p class="hint">Records must match these criteria to appear in the report.</p>
      <app-report-filter-editor [(filters)]="report.filters"></app-report-filter-editor>

      <div class="section-header">
        <h3>Default Sort</h3>
        <button class="button" (click)="addSort()">Add Sort</button>
      </div>
      <div class="sort-row" *ngFor="let sort of report.sorts; let i = index">
        <input type="text" placeholder="field path" [(ngModel)]="sort.path" />
        <select [(ngModel)]="sort.direction">
          <option [ngValue]="1">Ascending</option>
          <option [ngValue]="-1">Descending</option>
        </select>
        <button class="button small red" (click)="removeSort(i)">Remove</button>
      </div>
    </app-tabpage>

    <app-tabpage title="Display &amp; Routing">
      <div class="form-grid">
        <label>
          Item Type
          <input type="text" placeholder="detail route type, e.g. customer" [(ngModel)]="report.itemType" />
        </label>
        <label>
          View Route
          <input type="text" placeholder="custom detail route" [(ngModel)]="report.viewRoute" />
        </label>
        <label>
          Add Route
          <input type="text" placeholder="API route to add documents" [(ngModel)]="report.addRoute" />
        </label>
        <label>
          Add Permission
          <input type="text" placeholder="permission required to add" [(ngModel)]="report.addPermission" />
        </label>
        <label>
          Context Type(s)
          <input type="text" placeholder="comma-separated contexts" [(ngModel)]="contextTypeText" />
        </label>
        <label>
          Default Template Key
          <input type="text" placeholder="default print template" [(ngModel)]="report.defaultTemplateKey" />
        </label>
        <label>
          Print Template Keys
          <input type="text" placeholder="comma-separated template keys" [(ngModel)]="printTemplateKeysText" />
        </label>
        <label>
          Template Keys
          <input type="text" placeholder="comma-separated template keys" [(ngModel)]="templateKeysText" />
        </label>
      </div>

      <div class="section-header">
        <h3>Add Data</h3>
      </div>
      <p class="hint">Values merged into new documents created from this report.</p>
      <app-key-value-editor [(data)]="report.addData"></app-key-value-editor>

      <div class="section-header">
        <h3>Settings</h3>
      </div>
      <p class="hint">Custom settings used by specialty report logic.</p>
      <app-key-value-editor [(data)]="report.settings"></app-key-value-editor>
    </app-tabpage>

    <app-tabpage title="Charts">
      <div class="section-header">
        <h3>Charts</h3>
        <button class="button" (click)="addChart()">Add Chart</button>
      </div>
      <p class="hint">Charts appear behind the chart icon in the report header. One chart opens directly; several offer a chooser. Reports have no charts unless added here.</p>
      <app-chart-editor *ngFor="let chart of report.charts; let i = index"
        [chart]="chart" [fields]="report.fields" [index]="i" [count]="report.charts.length"
        (remove)="removeChart(i)" (moveUp)="moveChart(i, -1)" (moveDown)="moveChart(i, 1)">
      </app-chart-editor>
    </app-tabpage>

    <app-tabpage title="Import">
      <div class="form-grid">
        <label>
          Import Route
          <input type="text" placeholder="route for data import" [(ngModel)]="report.importRoute" />
        </label>
        <label>
          Import Permission
          <input type="text" placeholder="permission required to import" [(ngModel)]="report.importPermission" />
        </label>
        <label>
          Import Type
          <input type="text" placeholder="import handler type" [(ngModel)]="report.importType" />
        </label>
      </div>
    </app-tabpage>
  </app-tabs>
</div>

<app-support *ngIf="supportActive"></app-support>
<app-settings *ngIf="settingsActive"></app-settings>
